import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import { NewSubGoalRow } from 'shared/components/ui/new-sub-goal-row';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { SortingMode } from 'shared/types/sorting-mode';
import { TaskCardTask } from 'shared/types/task-card-task';
import {
  getOtherSectionId,
  isOtherSection,
} from 'shared/utils/goal-section-id-helpers';
import { sortGoals } from 'shared/utils/sort-goals';
import { sortItems } from 'shared/utils/sort-items';

import { GoalSectionsList } from './goal-sections-list';
import { GoalsList } from './goals-list';
import * as Styled from './task-goal-section-list.style';
import { TasksList } from './tasks-list';

export type GoalSectionTaskListProps = Omit<
  React.ComponentProps<typeof GoalsList>,
  'goals' | 'parentId'
> &
  Omit<
    React.ComponentProps<typeof GoalSectionsList>,
    'sections' | 'goalId' | 'isFrozen'
  > &
  Omit<React.ComponentProps<typeof TasksList>, 'tasks'> & {
    goalId: ID;
    sectionId?: ID;
    newTaskInputRef?: React.RefObject<HTMLDivElement>;
    onCreateSubGoal?: () => void;
    canCreateTask?: boolean;
  };

export const GoalSectionTaskList: React.FC<GoalSectionTaskListProps> = ({
  goalId,
  sectionId,
  newTaskInputRef,
  onCreateSubGoal,
  canCreateTask,

  allGoals,
  lifeAreas,
  onUpdateProgress,
  onPremium,
  onUpdateSection,
  onTask,
  onUnfocus,
  hideGoal,
  showRepeatingAsTodo,
  showRepeatingAsDoneOnDateEntry,
  selectedItem,
  canCollapse,
  noRightSpacing,

  items,
  hasDnd,
}) => {
  const { t } = useTranslation();

  const goal = useMemo(
    () => items.find(({ id }) => id === goalId) as Goal | undefined,
    [goalId, items],
  );

  const section = useMemo(
    () =>
      sectionId
        ? goal?.sections?.find((section) => section.id === sectionId)
        : undefined,
    [goal, sectionId],
  );

  const ownGoals = useMemo(
    () =>
      !sectionId
        ? sortGoals(
            items.filter(
              (item) =>
                !('fields' in item) &&
                item.parentIds?.[item.parentIds.length - 1] === goalId,
            ) as Goal[],
            {
              allGoals,
              sortMode: goal?.goalSortingMode,
              customOrder: goal?.goalSorting,
              invertStartDateSorting: true,
            },
          )
        : [],
    [
      allGoals,
      goal?.goalSorting,
      goal?.goalSortingMode,
      goalId,
      items,
      sectionId,
    ],
  );

  const ownTasks = useMemo(
    () =>
      sortItems(
        items.filter((item) => {
          // filter out non-tasks
          if (!('fields' in item)) {
            return false;
          }

          // if `other` section, take everything that is not in sections
          if (sectionId && isOtherSection(sectionId)) {
            return !goal?.sections?.some(({ tasks }) =>
              tasks?.includes(item.id),
            );
          }

          return section ? section.tasks?.includes(item.id) : true;
        }) as TaskCardTask[],
        goal?.taskSortingMode === SortingMode.Custom
          ? section?.tasks ?? goal?.taskSorting ?? []
          : [],
        'id',
      ),
    [
      goal?.sections,
      goal?.taskSorting,
      goal?.taskSortingMode,
      items,
      section,
      sectionId,
    ],
  );

  const ownSections = useMemo(
    () =>
      !sectionId && goal?.sections?.length
        ? [
            ...goal.sections,
            {
              id: getOtherSectionId(goalId),
              tasks: sortItems(
                ownTasks.filter(
                  ({ id }) =>
                    !goal.sections?.find(({ tasks }) => tasks?.includes(id)),
                ),
                goal.taskSorting ?? [],
                'id',
              ).map(({ id }) => id),
              name: t('general.labels.goal.sections.other.label'),
              createdAt: new Date(),
            },
          ]
        : [],
    [goal?.sections, goal?.taskSorting, goalId, ownTasks, sectionId, t],
  );

  return (
    <>
      {!!ownGoals.length && (
        <GoalsList
          goals={ownGoals}
          allGoals={allGoals}
          lifeAreas={lifeAreas}
          onUpdateProgress={onUpdateProgress}
          canCreateTask={canCreateTask}
          hasDnd={hasDnd}
          noRightSpacing={noRightSpacing}
        />
      )}
      {!!ownSections.length && (
        <GoalSectionsList
          goalId={goalId}
          sections={ownSections}
          items={items}
          allGoals={allGoals}
          lifeAreas={lifeAreas}
          onPremium={onPremium}
          onUpdateSection={onUpdateSection}
          onTask={onTask}
          onUnfocus={onUnfocus}
          hideGoal={hideGoal}
          showRepeatingAsTodo={showRepeatingAsTodo}
          showRepeatingAsDoneOnDateEntry={showRepeatingAsDoneOnDateEntry}
          selectedItem={selectedItem}
          isFrozen={!!goal?.frozenAt}
          canCreateTask={canCreateTask}
          canCollapse={canCollapse}
          hasDnd={hasDnd}
          noRightSpacing={noRightSpacing}
        />
      )}
      {!ownSections.length && (
        <TasksList
          tasks={ownTasks}
          showRepeatingAsTodo={showRepeatingAsTodo}
          showRepeatingAsDoneOnDateEntry={showRepeatingAsDoneOnDateEntry}
          selectedItem={selectedItem}
          onTask={onTask}
          onUnfocus={onUnfocus}
          hideGoal={hideGoal}
          hasIndentation={!sectionId}
          hasDnd={hasDnd}
          noRightSpacing={noRightSpacing}
        />
      )}

      {(!!onCreateSubGoal || !!canCreateTask) && (
        <Styled.DroppableArea>
          {!!canCreateTask && !ownSections.length && (
            <Styled.NewTaskContainer $hasIndentation={!!sectionId}>
              <NewTaskRow
                goal={goalId}
                section={sectionId}
                newTaskInputRef={newTaskInputRef}
              />
            </Styled.NewTaskContainer>
          )}
          {!!onCreateSubGoal && (
            <Styled.NewTaskContainer $hasIndentation={!!sectionId}>
              <NewSubGoalRow onCreateGoal={onCreateSubGoal} />
            </Styled.NewTaskContainer>
          )}
        </Styled.DroppableArea>
      )}
    </>
  );
};
